'use client';

import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import type { ToolLiveConfig } from '@/lib/tools';

type PresetId = 'google' | 'meta' | 'tiktok' | 'amazon' | 'custom';
type DynamicHelpKey = 'google' | 'meta' | 'tiktok' | 'amazon' | null;

type LinkBuildState = {
  baseUrlState: {
    normalized: string;
    parsed: URL | null;
    warning: string | null;
  };
  requiredParams: Record<'utm_source' | 'utm_medium' | 'utm_campaign', string>;
  optionalParams: Record<'utm_term' | 'utm_content', string>;
  missingRequiredFields: string[];
  warnings: string[];
  finalUrl: string;
  draftOutput: string;
  resultLabel: string;
};

const PLACEHOLDER_URL = 'https://your-site.com/landing-page';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const DYNAMIC_PLACEHOLDER_PATTERN = /(\{\{[^{}]+\}\}|\{[^{}]+\}|__[A-Za-z0-9_]+__)/g;

const presetOptions: Array<{
  id: PresetId;
  label: string;
  hint: string;
  defaults: { source: string; medium: string };
}> = [
  {
    id: 'google',
    label: 'Google',
    hint: 'google / cpc',
    defaults: { source: 'google', medium: 'cpc' },
  },
  {
    id: 'meta',
    label: 'Meta',
    hint: 'meta / paid_social',
    defaults: { source: 'meta', medium: 'paid_social' },
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    hint: 'tiktok / paid_social',
    defaults: { source: 'tiktok', medium: 'paid_social' },
  },
  {
    id: 'amazon',
    label: 'Amazon',
    hint: 'amazon / cpc',
    defaults: { source: 'amazon', medium: 'cpc' },
  },
  {
    id: 'custom',
    label: 'Custom',
    hint: 'Set your own naming',
    defaults: { source: '', medium: '' },
  },
];

const dynamicHelp = {
  google: {
    link: 'https://support.google.com/google-ads/answer/6305348',
    label: 'View dynamic params (Google)',
    example: '{campaignid} {adgroupid} {keyword}',
    note: 'Best for manual UTM tracking. If auto-tagging is on, UTMs may be secondary.',
  },
  meta: {
    link: 'https://www.facebook.com/business/help',
    label: 'View dynamic params (Meta)',
    example: '{{campaign.name}} {{adset.name}} {{ad.name}}',
  },
  tiktok: {
    link: 'https://ads.tiktok.com/help/article/track-offsite-web-events-with-utm-parameters',
    label: 'View dynamic params (TikTok)',
    example: '__CAMPAIGN_NAME__ __AID_NAME__ __CID_NAME__',
    note: 'Useful for TikTok Ads manual UTM tracking with platform macros.',
  },
  amazon: {
    link: 'https://advertising.amazon.com/resources/ad-policy/mmp-measurement-urls',
    label: 'View dynamic params (Amazon)',
    example: '{campaignid} {adgroupid} {adid} {lpurl}',
    note: '{lpurl} is usually used in tracking templates, not as a UTM field value.',
  },
} as const;

const quickPresets = [
  {
    id: 'google-dynamic',
    label: 'Google Ads dynamic',
    mode: 'google' as PresetId,
    source: 'google',
    medium: 'cpc',
    campaign: '{campaignid}',
    content: '{creative}',
    term: '{keyword}',
    helpKey: 'google' as DynamicHelpKey,
  },
  {
    id: 'meta-dynamic',
    label: 'Meta Ads dynamic',
    mode: 'meta' as PresetId,
    source: 'meta',
    medium: 'paid_social',
    campaign: '{{campaign.name}}',
    content: '{{ad.name}}',
    term: '{{adset.name}}',
    helpKey: 'meta' as DynamicHelpKey,
  },
  {
    id: 'custom-blank',
    label: 'Custom blank',
    mode: 'custom' as PresetId,
    source: '',
    medium: '',
    campaign: '',
    content: '',
    term: '',
    helpKey: null as DynamicHelpKey,
  },
  {
    id: 'tiktok-dynamic',
    label: 'TikTok Ads dynamic',
    mode: 'tiktok' as PresetId,
    source: 'tiktok',
    medium: 'paid_social',
    campaign: '__CAMPAIGN_NAME__',
    content: '__CID_NAME__',
    term: '__AID_NAME__',
    helpKey: 'tiktok' as DynamicHelpKey,
  },
  {
    id: 'amazon-dynamic',
    label: 'Amazon Ads dynamic',
    mode: 'amazon' as PresetId,
    source: 'amazon',
    medium: 'cpc',
    campaign: '{campaignid}',
    content: '{adid}',
    term: '{adgroupid}',
    helpKey: 'amazon' as DynamicHelpKey,
  },
] as const;

function getPresetOption(presetId: PresetId) {
  return presetOptions.find((option) => option.id === presetId) ?? presetOptions[0];
}

function getDynamicHelpKeyForPreset(presetId: PresetId): DynamicHelpKey {
  return presetId === 'google' ||
    presetId === 'meta' ||
    presetId === 'tiktok' ||
    presetId === 'amazon'
    ? presetId
    : null;
}

function decodeUtmInput(value: string) {
  const plusNormalized = value.replace(/\+/g, '%20');

  try {
    return decodeURIComponent(plusNormalized);
  } catch {
    return value;
  }
}

function isDynamicPlaceholder(value: string) {
  return /^(\{\{[^{}]+\}\}|\{[^{}]+\}|__[A-Za-z0-9_]+__)$/.test(value);
}

function normalizeStaticUtmSegment(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9._~-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function normalizeUtmValue(value: string) {
  const decoded = decodeUtmInput(value.trim());

  if (!decoded) {
    return '';
  }

  return decoded
    .split(DYNAMIC_PLACEHOLDER_PATTERN)
    .filter((segment) => segment.length > 0)
    .map((segment) =>
      isDynamicPlaceholder(segment) ? segment : normalizeStaticUtmSegment(segment),
    )
    .join('')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function serializeUtmValue(value: string) {
  const normalized = normalizeUtmValue(value);

  if (!normalized) {
    return '';
  }

  return normalized
    .split(DYNAMIC_PLACEHOLDER_PATTERN)
    .filter((segment) => segment.length > 0)
    .map((segment) => (isDynamicPlaceholder(segment) ? segment : encodeURIComponent(segment)))
    .join('');
}

function buildFinalUrl(
  parsedUrl: URL,
  params: Record<'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_term' | 'utm_content', string>,
) {
  const queryParts: string[] = [];

  for (const [key, value] of parsedUrl.searchParams.entries()) {
    if (UTM_KEYS.includes(key as (typeof UTM_KEYS)[number])) {
      continue;
    }

    queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }

  for (const [key, value] of Object.entries(params)) {
    if (!value) {
      continue;
    }

    queryParts.push(`${encodeURIComponent(key)}=${serializeUtmValue(value)}`);
  }

  const queryString = queryParts.length ? `?${queryParts.join('&')}` : '';
  return `${parsedUrl.origin}${parsedUrl.pathname}${queryString}${parsedUrl.hash}`;
}

function normalizeBaseUrl(value: string) {
  const trimmed = value.trim();

  if (!trimmed) {
    return { normalized: '', parsed: null, warning: null };
  }

  const normalized = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const parsed = new URL(normalized);

    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return {
        normalized,
        parsed: null,
        warning: 'Use a regular http or https destination URL.',
      };
    }

    return { normalized: parsed.toString(), parsed, warning: null };
  } catch {
    return {
      normalized,
      parsed: null,
      warning: 'Check the destination URL. It does not look valid yet.',
    };
  }
}

function inferPresetFromFields(source: string, medium: string): PresetId {
  const normalizedSource = normalizeUtmValue(source);
  const normalizedMedium = normalizeUtmValue(medium);

  if (normalizedSource === 'google' && normalizedMedium === 'cpc') {
    return 'google';
  }

  if (
    normalizedSource === 'meta' &&
    (normalizedMedium === 'paid-social' || normalizedMedium === 'paid_social')
  ) {
    return 'meta';
  }

  if (normalizedSource === 'tiktok' && normalizedMedium === 'paid_social') {
    return 'tiktok';
  }

  if (normalizedSource === 'amazon' && normalizedMedium === 'cpc') {
    return 'amazon';
  }

  return 'custom';
}

function buildLinkState(input: {
  baseUrl: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
}): LinkBuildState {
  const baseUrlState = normalizeBaseUrl(input.baseUrl);
  const requiredParams = {
    utm_source: normalizeUtmValue(input.utmSource),
    utm_medium: normalizeUtmValue(input.utmMedium),
    utm_campaign: normalizeUtmValue(input.utmCampaign),
  };
  const optionalParams = {
    utm_term: normalizeUtmValue(input.utmTerm),
    utm_content: normalizeUtmValue(input.utmContent),
  };
  const missingRequiredFields = Object.entries(requiredParams)
    .filter(([, value]) => !value)
    .map(([key]) => key);

  const warnings: string[] = [];

  if (baseUrlState.warning) {
    warnings.push(baseUrlState.warning);
  }

  if (baseUrlState.parsed) {
    const seenKeys = new Set<string>();

    for (const key of baseUrlState.parsed.searchParams.keys()) {
      const normalizedKey = key.toLowerCase();

      if (seenKeys.has(normalizedKey)) {
        warnings.push(`Duplicate query parameter detected: ${key}`);
        continue;
      }

      seenKeys.add(normalizedKey);

      if (normalizedKey.startsWith('utm_')) {
        warnings.push(`Existing ${normalizedKey} will be replaced in the final link.`);
      }
    }
  }

  let finalUrl = '';

  if (baseUrlState.parsed) {
    finalUrl = buildFinalUrl(baseUrlState.parsed, { ...requiredParams, ...optionalParams });
  }

  const draftOutput =
    finalUrl || baseUrlState.normalized || input.baseUrl.trim() || PLACEHOLDER_URL;

  let resultLabel = 'Start with a destination URL.';

  if (input.baseUrl.trim() && baseUrlState.warning) {
    resultLabel = 'Draft only. Fix the destination URL before publishing.';
  } else if (input.baseUrl.trim() && missingRequiredFields.length) {
    resultLabel = 'Draft link ready. Fill the missing fields before launch.';
  } else if (input.baseUrl.trim() && baseUrlState.parsed) {
    resultLabel = 'Ready to copy and publish.';
  }

  return {
    baseUrlState,
    requiredParams,
    optionalParams,
    missingRequiredFields,
    warnings: Array.from(new Set(warnings)),
    finalUrl,
    draftOutput,
    resultLabel,
  };
}

function downloadTextFile(content: string, fileName: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function copyToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

type ResultPanelProps = {
  canExportText: boolean;
  copyLabel: string;
  draftOutput: string;
  onCopy: () => void;
  onExport: () => void;
  resultLabel: string;
  warnings: string[];
  hasExistingQuery: boolean;
  statusTone: 'idle' | 'invalid' | 'warning' | 'ready';
  actionMessage: string | null;
  actionTone: 'neutral' | 'success' | 'warning';
};

const ResultPanel = memo(function ResultPanel({
  canExportText,
  copyLabel,
  draftOutput,
  onCopy,
  onExport,
  resultLabel,
  warnings,
  hasExistingQuery,
  statusTone,
  actionMessage,
  actionTone,
}: ResultPanelProps) {
  return (
    <aside className="tool-live-panel tool-utm-result-panel">
      <div className="tool-utm-result-card tool-utm-result-card-prominent">
        <div className="tool-utm-result-header">
          <div>
            <span className="eyebrow">Result</span>
            <strong>{copyLabel}</strong>
          </div>
        </div>

        <div className="tool-utm-result-shell">
          <div className="tool-utm-result-url tool-utm-result-url-prominent">{draftOutput}</div>
          <button
            aria-label="Copy URL"
            className="tool-utm-copy-inline"
            onClick={onCopy}
            type="button"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M9 9.5A2.5 2.5 0 0 1 11.5 7h6A2.5 2.5 0 0 1 20 9.5v6a2.5 2.5 0 0 1-2.5 2.5h-6A2.5 2.5 0 0 1 9 15.5v-6Z"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.7"
              />
              <path
                d="M15 7V6.5A2.5 2.5 0 0 0 12.5 4h-6A2.5 2.5 0 0 0 4 6.5v6A2.5 2.5 0 0 0 6.5 15H7"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.7"
              />
            </svg>
          </button>
        </div>

        <div className="tool-utm-result-meta">
          <span
            className={`tool-utm-status ${
              statusTone === 'ready'
                ? 'tool-utm-status-ready'
                : statusTone === 'warning'
                  ? 'tool-utm-status-warning'
                  : statusTone === 'invalid'
                    ? 'tool-utm-status-invalid'
                    : 'tool-utm-status-idle'
            }`}
          >
            {resultLabel}
          </span>
          <span className="tool-live-stat-chip">
            {hasExistingQuery ? 'Existing query kept' : 'No existing query'}
          </span>
        </div>

        {warnings.length ? (
          <ul className="tool-utm-warning-list tool-utm-warning-list-compact">
            {warnings.slice(0, 3).map((warning) => (
              <li className="tool-batch-stage-note" key={warning}>
                <strong>Check</strong>
                <span>{warning}</span>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="tool-live-actions">
          <button className="button" onClick={onCopy} type="button">
            Copy final URL
          </button>
          {canExportText ? (
            <button className="button button-ghost" onClick={onExport} type="button">
              Export .txt
            </button>
          ) : null}
        </div>

        {actionMessage ? (
          <div
            className={`tool-batch-stage-note ${
              actionTone === 'success'
                ? 'tool-utm-stage-note-success'
                : actionTone === 'warning'
                  ? 'tool-utm-stage-note-warning'
                  : ''
            }`}
          >
            <strong>
              {actionTone === 'success' ? 'Done' : actionTone === 'warning' ? 'Check' : 'Note'}
            </strong>
            <span>{actionMessage}</span>
          </div>
        ) : null}
      </div>
    </aside>
  );
});

type BrowserUtmLinkBuilderProps = {
  variant?: ToolLiveConfig;
};

export default function BrowserUtmLinkBuilder({ variant }: BrowserUtmLinkBuilderProps) {
  const urlRef = useRef<HTMLInputElement | null>(null);
  const sourceRef = useRef<HTMLInputElement | null>(null);
  const mediumRef = useRef<HTMLInputElement | null>(null);
  const campaignRef = useRef<HTMLInputElement | null>(null);
  const contentRef = useRef<HTMLInputElement | null>(null);
  const termRef = useRef<HTMLInputElement | null>(null);

  const initialPresetId = variant?.defaultUtmPresetId ?? 'google';
  const initialPreset = getPresetOption(initialPresetId);
  const visibleTitle = variant?.defaultUtmTitle ?? 'Build a clean campaign URL in one pass';
  const visibleSubtitle =
    variant?.defaultUtmSubtitle ??
    'Add the destination, choose a channel preset, fill the core UTM fields, and copy a campaign link that is ready to use.';

  const [baseUrl, setBaseUrl] = useState('');
  const [preset, setPreset] = useState<PresetId>(initialPreset.id);
  const [utmSource, setUtmSource] = useState(initialPreset.defaults.source);
  const [utmMedium, setUtmMedium] = useState(initialPreset.defaults.medium);
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmTerm, setUtmTerm] = useState('');
  const [utmContent, setUtmContent] = useState('');
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [showQuickPresets, setShowQuickPresets] = useState(false);
  const [activeDynamicHelp, setActiveDynamicHelp] = useState<DynamicHelpKey>(
    getDynamicHelpKeyForPreset(initialPreset.id),
  );
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionTone, setActionTone] = useState<'neutral' | 'success' | 'warning'>('neutral');

  useEffect(() => {
    urlRef.current?.focus();
  }, []);

  const currentState = useMemo(
    () =>
      buildLinkState({
        baseUrl,
        utmSource,
        utmMedium,
        utmCampaign,
        utmTerm,
        utmContent,
      }),
    [baseUrl, utmCampaign, utmContent, utmMedium, utmSource, utmTerm],
  );

  const canExportText = Boolean(
    currentState.baseUrlState.parsed &&
    currentState.missingRequiredFields.length === 0 &&
    currentState.finalUrl,
  );

  const statusTone: 'idle' | 'invalid' | 'warning' | 'ready' = !baseUrl.trim()
    ? 'idle'
    : currentState.baseUrlState.warning
      ? 'invalid'
      : currentState.missingRequiredFields.length
        ? 'warning'
        : 'ready';

  const dynamicLink = activeDynamicHelp ? dynamicHelp[activeDynamicHelp] : null;

  function moveFocus(next: RefObject<HTMLInputElement | null>) {
    next.current?.focus();
    next.current?.select();
  }

  function handleFieldKeyDown(
    event: KeyboardEvent<HTMLInputElement>,
    next?: RefObject<HTMLInputElement | null>,
  ) {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();

    if (next) {
      moveFocus(next);
    }
  }

  function applyPreset(nextPreset: PresetId) {
    const selectedPreset = getPresetOption(nextPreset);

    setPreset(nextPreset);
    setUtmSource(selectedPreset.defaults.source);
    setUtmMedium(selectedPreset.defaults.medium);
    setActiveDynamicHelp(getDynamicHelpKeyForPreset(nextPreset));
    setActionTone('neutral');
    setActionMessage(
      nextPreset === 'custom'
        ? 'Custom mode is active.'
        : `${selectedPreset.label} defaults applied.`,
    );
  }

  function applyImportedUrl(value: string) {
    const normalized = normalizeBaseUrl(value);

    if (!normalized.parsed) {
      setBaseUrl(value.trim());
      return;
    }

    const nextUrl = new URL(normalized.parsed.toString());
    const nextSource = nextUrl.searchParams.get('utm_source') ?? utmSource;
    const nextMedium = nextUrl.searchParams.get('utm_medium') ?? utmMedium;
    const nextCampaign = nextUrl.searchParams.get('utm_campaign') ?? utmCampaign;
    const nextTerm = nextUrl.searchParams.get('utm_term') ?? utmTerm;
    const nextContent = nextUrl.searchParams.get('utm_content') ?? utmContent;

    for (const key of UTM_KEYS) {
      nextUrl.searchParams.delete(key);
    }

    const inferredPreset = inferPresetFromFields(nextSource, nextMedium);

    setBaseUrl(nextUrl.toString());
    setUtmSource(nextSource);
    setUtmMedium(nextMedium);
    setUtmCampaign(nextCampaign);
    setUtmTerm(nextTerm);
    setUtmContent(nextContent);
    setPreset(inferredPreset);
    setActiveDynamicHelp(getDynamicHelpKeyForPreset(inferredPreset));
    setActionTone('neutral');
    setActionMessage(
      nextCampaign || nextSource || nextMedium
        ? 'Imported URL and detected existing UTM values.'
        : 'Imported destination URL.',
    );
  }

  function handleUrlPaste(event: ClipboardEvent<HTMLInputElement>) {
    const text = event.clipboardData.getData('text/plain').trim();
    if (!text || (!text.includes('.') && !text.includes('://'))) {
      return;
    }

    event.preventDefault();
    applyImportedUrl(text);
  }

  function resetForm() {
    setBaseUrl('');
    setPreset(initialPreset.id);
    setUtmSource(initialPreset.defaults.source);
    setUtmMedium(initialPreset.defaults.medium);
    setUtmCampaign('');
    setUtmTerm('');
    setUtmContent('');
    setActiveDynamicHelp(getDynamicHelpKeyForPreset(initialPreset.id));
    setActionTone('neutral');
    setActionMessage('Builder reset.');
    urlRef.current?.focus();
  }

  async function copyCurrentUrl() {
    const copyValue = currentState.draftOutput || PLACEHOLDER_URL;

    try {
      await copyToClipboard(copyValue);
      setActionTone('success');
      setActionMessage(
        currentState.finalUrl &&
          currentState.missingRequiredFields.length === 0 &&
          currentState.baseUrlState.parsed
          ? 'Final URL copied.'
          : currentState.baseUrlState.parsed
            ? 'Draft URL copied.'
            : 'Placeholder URL copied. Add your real destination before publishing.',
      );
    } catch {
      setActionTone('warning');
      setActionMessage('Copy failed in this browser. Select the URL manually and copy it.');
    }
  }

  function exportCurrentUrl() {
    if (!canExportText || !currentState.finalUrl) {
      return;
    }

    const baseName = currentState.requiredParams.utm_campaign || 'utm-link';
    downloadTextFile(currentState.finalUrl, `${baseName}.txt`);
    setActionTone('success');
    setActionMessage('TXT export downloaded.');
  }

  function applyQuickPreset(presetId: (typeof quickPresets)[number]['id']) {
    const selectedPreset = quickPresets.find((item) => item.id === presetId);
    if (!selectedPreset) {
      return;
    }

    setPreset(selectedPreset.mode);
    setUtmSource(selectedPreset.source);
    setUtmMedium(selectedPreset.medium);
    setUtmCampaign(selectedPreset.campaign);
    setUtmContent(selectedPreset.content);
    setUtmTerm(selectedPreset.term);
    setActiveDynamicHelp(selectedPreset.helpKey);
    setActionTone('neutral');
    setActionMessage(`${selectedPreset.label} applied.`);
  }

  return (
    <section className="tool-live-shell">
      <div className="article-meta-strip">
        <span>Browser-only builder</span>
        <span>{variant?.badgeLabel ?? 'Campaign links'}</span>
      </div>
      <h1 className="tool-live-title">{visibleTitle}</h1>
      <p className="tool-live-subtitle">{visibleSubtitle}</p>

      <div className="tool-utm-layout">
        <div className="tool-live-panel tool-utm-form-panel">
          <div className="tool-utm-step">
            <span className="eyebrow">1. URL</span>
            <label className="tool-utm-field">
              <span>Destination URL</span>
              <input
                autoFocus
                className="tool-utm-input"
                inputMode="url"
                onChange={(event) => setBaseUrl(event.target.value)}
                onKeyDown={(event) => handleFieldKeyDown(event, sourceRef)}
                onPaste={handleUrlPaste}
                placeholder="yourstore.com/products/product-name"
                ref={urlRef}
                type="text"
                value={baseUrl}
              />
              <small>Paste a full URL and we will keep non-UTM query params automatically.</small>
            </label>
          </div>

          <div className="tool-utm-step">
            <span className="eyebrow">2. Preset</span>
            <div className="tool-utm-pills" role="radiogroup" aria-label="Source preset">
              {presetOptions.map((option) => {
                const active = preset === option.id;
                return (
                  <button
                    className={`tool-utm-pill ${active ? 'tool-utm-pill-active' : ''}`}
                    key={option.id}
                    onClick={() => applyPreset(option.id)}
                    type="button"
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <span className="tool-utm-inline-hint">
              {presetOptions.find((option) => option.id === preset)?.hint}
            </span>
          </div>

          <div className="tool-utm-step">
            <span className="eyebrow">3. Required</span>
            <div className="tool-utm-fields-grid">
              <label className="tool-utm-field">
                <span>
                  `utm_source`
                  <span className="tool-utm-help-wrap">
                    <span className="tool-utm-help-dot" aria-hidden="true">
                      ?
                    </span>
                    <span className="tool-utm-help-bubble" role="tooltip">
                      Traffic platform or source.
                    </span>
                  </span>
                </span>
                <input
                  className="tool-utm-input"
                  onChange={(event) => setUtmSource(event.target.value)}
                  onKeyDown={(event) => handleFieldKeyDown(event, mediumRef)}
                  placeholder="google"
                  ref={sourceRef}
                  type="text"
                  value={utmSource}
                />
              </label>

              <label className="tool-utm-field">
                <span>
                  `utm_medium`
                  <span className="tool-utm-help-wrap">
                    <span className="tool-utm-help-dot" aria-hidden="true">
                      ?
                    </span>
                    <span className="tool-utm-help-bubble" role="tooltip">
                      Channel type like cpc or paid_social.
                    </span>
                  </span>
                </span>
                <input
                  className="tool-utm-input"
                  onChange={(event) => setUtmMedium(event.target.value)}
                  onKeyDown={(event) => handleFieldKeyDown(event, campaignRef)}
                  placeholder="cpc"
                  ref={mediumRef}
                  type="text"
                  value={utmMedium}
                />
              </label>

              <label className="tool-utm-field tool-utm-field-full">
                <span>
                  `utm_campaign`
                  <span className="tool-utm-help-wrap">
                    <span className="tool-utm-help-dot" aria-hidden="true">
                      ?
                    </span>
                    <span className="tool-utm-help-bubble" role="tooltip">
                      Campaign name.
                    </span>
                  </span>
                </span>
                <input
                  className="tool-utm-input"
                  onChange={(event) => setUtmCampaign(event.target.value)}
                  onKeyDown={(event) => handleFieldKeyDown(event, contentRef)}
                  placeholder="spring-launch"
                  ref={campaignRef}
                  type="text"
                  value={utmCampaign}
                />
                <small>Short, consistent names work best: `season-offer-market`.</small>
              </label>
            </div>
          </div>

          <div
            className={`tool-utm-collapsible ${showOptionalFields ? 'tool-utm-collapsible-open' : ''}`}
          >
            <button
              aria-expanded={showOptionalFields}
              className="tool-utm-collapsible-toggle"
              onClick={() => setShowOptionalFields((current) => !current)}
              type="button"
            >
              <span>Optional fields</span>
              <span>{showOptionalFields ? 'Hide' : 'Show'}</span>
            </button>
            <div className="tool-utm-collapsible-body">
              <div className="tool-utm-fields-grid">
                <label className="tool-utm-field">
                  <span>
                    `utm_content`
                    <span className="tool-utm-help-wrap">
                      <span className="tool-utm-help-dot" aria-hidden="true">
                        ?
                      </span>
                      <span className="tool-utm-help-bubble" role="tooltip">
                        Ad or creative variation.
                      </span>
                    </span>
                  </span>
                  <input
                    className="tool-utm-input"
                    onChange={(event) => setUtmContent(event.target.value)}
                    onKeyDown={(event) => handleFieldKeyDown(event, termRef)}
                    placeholder="cta-a or image-1"
                    ref={contentRef}
                    type="text"
                    value={utmContent}
                  />
                </label>
                <label className="tool-utm-field">
                  <span>
                    `utm_term`
                    <span className="tool-utm-help-wrap">
                      <span className="tool-utm-help-dot" aria-hidden="true">
                        ?
                      </span>
                      <span className="tool-utm-help-bubble" role="tooltip">
                        Keyword, audience, or ad set label.
                      </span>
                    </span>
                  </span>
                  <input
                    className="tool-utm-input"
                    onChange={(event) => setUtmTerm(event.target.value)}
                    onKeyDown={(event) => handleFieldKeyDown(event)}
                    placeholder="running-shoes"
                    ref={termRef}
                    type="text"
                    value={utmTerm}
                  />
                </label>
              </div>

              {dynamicLink ? (
                <div className="tool-utm-inline-note">
                  <strong>{dynamicLink.label}</strong>
                  <span>Common placeholders: {dynamicLink.example}</span>
                  {'note' in dynamicLink ? <span>{dynamicLink.note}</span> : null}
                  <a href={dynamicLink.link} target="_blank">
                    Open official help
                  </a>
                </div>
              ) : null}
            </div>
          </div>

          <div
            className={`tool-utm-collapsible ${showQuickPresets ? 'tool-utm-collapsible-open' : ''}`}
          >
            <button
              aria-expanded={showQuickPresets}
              className="tool-utm-collapsible-toggle"
              onClick={() => setShowQuickPresets((current) => !current)}
              type="button"
            >
              <span>Quick presets</span>
              <span>{showQuickPresets ? 'Hide' : 'Show'}</span>
            </button>
            <div className="tool-utm-collapsible-body">
              <div className="tool-utm-quick-preset-grid">
                {quickPresets.map((presetItem) => (
                  <button
                    className="tool-utm-quick-preset"
                    key={presetItem.id}
                    onClick={() => applyQuickPreset(presetItem.id)}
                    type="button"
                  >
                    <strong>{presetItem.label}</strong>
                    <span>
                      {presetItem.id === 'custom-blank'
                        ? 'Clear the UTM fields and start from scratch.'
                        : `${presetItem.source} / ${presetItem.medium}`}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <ResultPanel
          actionMessage={actionMessage}
          actionTone={actionTone}
          canExportText={canExportText}
          copyLabel={
            currentState.missingRequiredFields.length === 0 && currentState.baseUrlState.parsed
              ? 'Final URL'
              : 'Draft URL'
          }
          draftOutput={currentState.draftOutput}
          hasExistingQuery={Boolean(currentState.baseUrlState.parsed?.search)}
          onCopy={copyCurrentUrl}
          onExport={exportCurrentUrl}
          resultLabel={currentState.resultLabel}
          statusTone={statusTone}
          warnings={currentState.warnings}
        />
      </div>
    </section>
  );
}
